import { Scale, ExternalLink, ArrowLeft, ShieldCheck, Database, Brain, Globe, Lock, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo.png";
import SEO from "@/components/SEO";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <SEO
        title="Privacy Policy — GreyRock AI®"
        description="How GreyRock AI collects, processes and protects your data. Messages are analyzed privately, never sold, and never used to train external models without opt-in."
        path="/privacy"
      />
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)",
        }}
      />

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-cyan transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <img src={logo} alt="GreyRock AI" className="w-10 h-10 object-contain" />
          <span className="font-bold text-lg">
            <span className="text-foreground">Grey</span>
            <span className="text-cyan">Rock</span>
            <span className="text-foreground"> AI</span>
            <sup className="text-[10px] text-muted-foreground">&reg;</sup>
          </span>
        </div>

        <h1 className="text-3xl font-bold">
          <span className="text-foreground">Privacy </span>
          <span className="text-cyan">Policy</span>
        </h1>

        <p className="text-muted-foreground text-sm">Last Updated: September 27, 2026</p>

        <p className="text-muted-foreground leading-relaxed">
          This policy governs the use of GreyRock AI&reg;, a forensic text analysis platform owned and operated by Should I Studios LLC.
        </p>

        {/* Section 1 */}
        <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <ShieldCheck className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-bold text-lg text-foreground">1. Our Privacy-First Approach</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            GreyRock AI&reg; is designed to help you understand difficult communication without selling your personal information or using your private messages to train our AI models. We limit access to saved information and give account holders tools to delete their data.
          </p>
        </div>

        {/* Section 2 */}
        <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Database className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-bold text-lg text-foreground">2. Information We Process and Store</h2>
          </div>
          <div className="space-y-3 text-muted-foreground leading-relaxed">
            <p><strong className="text-foreground">Message and Analysis Data:</strong> When a signed-in user submits a message for a paid analysis, we store the original message and the resulting analysis so the user can return to saved reports and create exports. This may include scores, detected communication patterns, strategy guidance, and generated responses.</p>
            <p><strong className="text-foreground">Vault and Rehearsal Data:</strong> If you use document storage or Practice Conversations, we store the files, session messages, and related analysis needed to provide those features and make them available in your account.</p>
            <p><strong className="text-foreground">Free Analysis:</strong> The logged-out free analyzer matches text in your browser against a public pattern library. We record limited usage information, such as message length, result category, and remaining uses, but not the message itself.</p>
            <p><strong className="text-foreground">Account and Usage Data:</strong> We store information needed to operate your account, including your email address, payment and credit status, saved settings, attribution information, and first-party product usage events. Analytics events do not contain the messages you submit for analysis.</p>
            <p><strong className="text-foreground">Retention:</strong> Account content remains available until you delete it through the available account controls or request deletion, unless we must retain limited records to meet legal, security, or fraud-prevention obligations.</p>
          </div>
        </div>

        {/* Section 3 */}
        <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Brain className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-bold text-lg text-foreground">3. No AI Training on User Data</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            We do not use your private messages to train our AI models. AI providers process submitted content only to generate the analysis or response you request. GreyRock AI&reg; does not sell message content or personal information to data brokers.
          </p>
        </div>

        {/* Section 4 */}
        <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Globe className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-bold text-lg text-foreground">4. Your Choices and Rights</h2>
          </div>
          <div className="space-y-3 text-muted-foreground leading-relaxed">
            <p><strong className="text-foreground">Automated Decision-Making:</strong> GreyRock AI&reg; provides suggestions, not binding decisions. You maintain full human oversight.</p>
            <p><strong className="text-foreground">Access and Deletion:</strong> You can access saved content through your account. You can also delete your account and associated analyses, Vault documents, rehearsal sessions, usage records, and related account data through Settings, or contact us for help.</p>
            <p><strong className="text-foreground">Former Community Feed:</strong> GreyRock AI&reg; no longer publishes analyses to a public feed. Anonymized entries submitted before the feed was retired are no longer visible to the public; they are kept privately, are accessible only to authorized administrators, and are not linked to your account.</p>
            <p><strong className="text-foreground">Sensitive Data:</strong> We do not knowingly collect &ldquo;Neural Data&rdquo; or biometric identifiers.</p>
          </div>
        </div>

        {/* Section 5 */}
        <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Lock className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-bold text-lg text-foreground">5. Third-Party Services</h2>
          </div>
          <div className="space-y-3 text-muted-foreground leading-relaxed">
            <p><strong className="text-foreground">Payments:</strong> Stripe processes payments. GreyRock AI&reg; does not receive or store your full payment-card number.</p>
            <p><strong className="text-foreground">AI Processing:</strong> We send submitted content to the AI service providers needed to produce the analysis or response you request. We do not permit your content to be used to train their general-purpose models.</p>
            <p><strong className="text-foreground">Analytics:</strong> Product analytics are first-party and stored in our own systems. We do not use third-party advertising pixels, session-replay tools, or data-broker tracking.</p>
            <p><strong className="text-foreground">Directory Badge Clicks:</strong> We count clicks on homepage directory badges as daily totals for each directory, not unique visitors. These counters do not store user or session IDs, message content, or individual click records, and honor Do Not Track and Global Privacy Control. Hosting providers may still process ordinary request logs.</p>
          </div>
        </div>

        {/* Section 6 */}
        <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Mail className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-bold text-lg text-foreground">6. Contact Us</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Questions about your data? Email us at: <a href="mailto:support@greyrockai.app" className="text-cyan hover:underline">support@greyrockai.app</a>
          </p>
        </div>

        {/* Full Policy Link */}
        <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Scale className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-semibold text-lg text-foreground">Primary Privacy Policy</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            GreyRock AI&reg; is a product of Should I Studios LLC. For our full corporate data handling practices, please view our Primary Privacy Policy at the link below.
          </p>
          <a
            href="https://shouldistudios.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-cyan hover:underline"
          >
            Full Privacy Policy <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <footer className="pt-8 border-t border-glass-border text-center space-y-2">
          <p className="text-xs text-muted-foreground">
            &copy; 2026 GreyRock AI&reg;. All Rights Reserved.
          </p>
          <p className="text-xs text-muted-foreground">
            GreyRock AI&reg; is a brand of Should I Studios LLC.
          </p>
          <p className="text-xs text-muted-foreground">
            2340 E. Trinity Mills Road, Suite 300, Carrollton, TX, 75006
          </p>
          <p className="text-xs text-muted-foreground">
            Contact: support@greyrockai.app
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-cyan hover:underline text-sm mt-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </footer>
      </div>
    </div>
  );
};

export default Privacy;
